import React, { useState, useEffect, useCallback } from 'react';
import { borderRadius, shadows, transitions, breakpoints } from '../../designTokens';

const aspectRatios = {
  '16:9': { paddingBottom: '56.25%' },
  '4:3': { paddingBottom: '75%' },
  '1:1': { paddingBottom: '100%' },
  '3:2': { paddingBottom: '66.67%' },
  '2:1': { paddingBottom: '50%' },
  cover: { height: '100%', position: 'absolute', inset: 0 },
  contain: { height: '100%', position: 'relative' },
};

const radiusMap = {
  xs: borderRadius.xs,
  sm: borderRadius.sm,
  md: borderRadius.md,
  lg: borderRadius.lg,
  xl: borderRadius.xl,
  pill: borderRadius.pill,
};

const variantStyles = {
  elevated: { background: 'white', boxShadow: shadows.lg, border: 'none' },
  outlined: { background: 'transparent', boxShadow: 'none', border: `1.5px solid #e5e0d8` },
  flat: { background: 'transparent', boxShadow: 'none', border: 'none' },
};

const hoverEffects = {
  lift: { transform: 'translateY(-4px) scale(1.02)', boxShadow: shadows.xl },
  scale: { transform: 'scale(1.03)', boxShadow: undefined },
  shadow: { transform: undefined, boxShadow: shadows.xl },
  none: {},
};

const imagePositionLayout = {
  top: { display: 'block', imageWidth: '100%', direction: 'column' },
  left: { display: 'flex', imageWidth: '40%', direction: 'row' },
  right: { display: 'flex', imageWidth: '40%', direction: 'row-reverse' },
  background: { display: 'block', imageWidth: '100%', direction: 'column' },
};

const badgePositions = {
  'top-left': { top: 12, left: 12 },
  'top-right': { top: 12, right: 12 },
  'bottom-left': { bottom: 12, left: 12 },
  'bottom-right': { bottom: 12, right: 12 },
};

function CardSkeleton() {
  return (
    <div style={{ padding: '20px 22px' }}>
      <div style={{
        width: '60%', height: 14, borderRadius: 4,
        background: '#e5e0d8', marginBottom: 12,
        animation: 'pulse 1.5s ease-in-out infinite',
      }} />
      <div style={{
        width: '40%', height: 10, borderRadius: 4,
        background: '#e5e0d8', marginBottom: 8,
        animation: 'pulse 1.5s ease-in-out infinite',
      }} />
      <div style={{
        width: '80%', height: 10, borderRadius: 4,
        background: '#e5e0d8',
        animation: 'pulse 1.5s ease-in-out infinite',
      }} />
    </div>
  );
}

function CardBadge({ children, position }) {
  return (
    <div
      style={{
        position: 'absolute', zIndex: 2,
        ...badgePositions[position] || badgePositions['top-right'],
      }}
    >
      {children}
    </div>
  );
}

export default function Card({
  children,
  image,
  aspectRatio = '16:9',
  layout,
  imagePosition: imagePositionProp,
  onClick,
  style,
  variant = 'elevated',
  radius = 'xl',
  hoverEffect = 'lift',
  loading,
  badge,
  badgePosition = 'top-right',
  actions,
  clickable,
  header,
  footer,
  imageOverlay,
  shadow,
  padding,
  imageObjectFit = 'cover',
  disabled,
  showFocusRing = true,
  focusRingColor,
  imageFallback,
  imageLoading = 'lazy',
  transitionDuration = 'normal',
  hoverStyle: customHoverStyle,
  contentAlign = 'left',
  fullHeight,
  onHover,
  imageZoom = true,
  responsiveLayout: responsiveLayoutProp,
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [activeResponsiveLayout, setActiveResponsiveLayout] = useState(null);

  const transitionValue = transitions[transitionDuration] || transitionDuration || transitions.normal;
  const radiusValue = radiusMap[radius] || radiusMap.xl;
  const shadowValue = shadow ? (shadows[shadow] || shadow) : variantStyles[variant]?.boxShadow || shadows.lg;
  const imgPosition = activeResponsiveLayout || imagePositionProp || (layout === 'horizontal' ? 'left' : 'top');
  const isClickable = clickable !== undefined ? clickable : !!onClick;
  const isBackground = imgPosition === 'background';
  const imagePos = imagePositionLayout[imgPosition] || imagePositionLayout.top;

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = (e) => setReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (!responsiveLayoutProp) { setActiveResponsiveLayout(null); return; }
    const check = () => {
      const w = window.innerWidth;
      const bp = Object.keys(breakpoints)
        .sort((a, b) => parseFloat(breakpoints[a]) - parseFloat(breakpoints[b]));
      let matched = null;
      for (const key of bp) {
        if (w >= parseFloat(breakpoints[key]) && responsiveLayoutProp[key]) {
          matched = responsiveLayoutProp[key];
        }
      }
      setActiveResponsiveLayout(matched);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [responsiveLayoutProp]);

  useEffect(() => {
    setImgError(false);
  }, [image]);

  const handleKeyDown = useCallback((e) => {
    if (!isClickable || disabled || loading) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(e);
    }
  }, [isClickable, disabled, loading, onClick]);

  const handleMouseEnter = useCallback((e) => {
    if (disabled || loading) return;
    const effect = reducedMotion ? hoverEffects.none : (hoverEffects[hoverEffect] || hoverEffects.lift);
    if (effect.transform) e.currentTarget.style.transform = effect.transform;
    if (effect.boxShadow) e.currentTarget.style.boxShadow = effect.boxShadow;
    if (customHoverStyle) Object.assign(e.currentTarget.style, customHoverStyle);
    onHover?.(true);
  }, [disabled, loading, hoverEffect, reducedMotion, customHoverStyle, onHover]);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.transform = 'translateY(0) scale(1)';
    e.currentTarget.style.boxShadow = shadowValue;
    onHover?.(false);
  }, [shadowValue, onHover]);

  const handleImageError = useCallback(() => {
    setImgError(true);
  }, []);

  const cardPadding = padding || '20px 22px';
  const baseTransition = reducedMotion ? 'none' : `all ${transitionValue}`;

  const wrapperStyle = {
    background: variantStyles[variant]?.background || 'white',
    borderRadius: radiusValue,
    overflow: 'hidden',
    boxShadow: disabled ? 'none' : shadowValue,
    border: variantStyles[variant]?.border || 'none',
    cursor: disabled ? 'not-allowed' : isClickable ? 'pointer' : 'default',
    transition: baseTransition,
    display: imagePos.display,
    flexDirection: imagePos.direction,
    opacity: disabled ? 0.5 : 1,
    pointerEvents: disabled ? 'none' : undefined,
    height: fullHeight ? '100%' : undefined,
    textAlign: contentAlign,
    position: 'relative',
    ...(isFocused && showFocusRing && isClickable ? {
      outline: 'none',
      boxShadow: `0 0 0 2px ${focusRingColor || '#1a6b4a'}`,
    } : {}),
    ...style,
  };

  const imageContainerStyle = {
    position: 'relative',
    overflow: 'hidden',
    flexShrink: 0,
    ...(isBackground
      ? { position: 'absolute', inset: 0, zIndex: 0 }
      : imagePos.display === 'flex'
        ? { width: imagePos.imageWidth, minHeight: 180 }
        : aspectRatios[aspectRatio] || aspectRatios['16:9']
    ),
  };

  const imgStyle = {
    position: imagePos.display === 'flex' || isBackground ? 'absolute' : 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: imageObjectFit,
    transition: imageZoom && !reducedMotion ? `transform ${transitions.slow}` : 'none',
  };

  const contentStyle = {
    flex: 1,
    padding: cardPadding,
    position: isBackground ? 'relative' : undefined,
    zIndex: isBackground ? 1 : undefined,
  };

  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={disabled || loading ? undefined : onClick}
      onKeyDown={handleKeyDown}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={wrapperStyle}
      aria-disabled={disabled}
    >
      {loading ? (
        <CardSkeleton />
      ) : (
        <>
          {image && !imgError && (
            <div style={imageContainerStyle}>
              <img
                src={image}
                alt=""
                loading={imageLoading}
                style={imgStyle}
                onError={handleImageError}
                onMouseEnter={imageZoom && !reducedMotion ? (e) => { e.currentTarget.style.transform = 'scale(1.05)'; } : undefined}
                onMouseLeave={imageZoom && !reducedMotion ? (e) => { e.currentTarget.style.transform = 'scale(1)'; } : undefined}
              />
              {isBackground && (
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(0,0,0,0.35)',
                  zIndex: 1,
                }} />
              )}
              {imageOverlay && (
                <div style={{ position: 'absolute', inset: 0, zIndex: 2 }}>
                  {imageOverlay}
                </div>
              )}
            </div>
          )}
          {imgError && imageFallback && (
            <div style={imageContainerStyle}>
              {typeof imageFallback === 'string' ? (
                <div style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: '#f5f5f5', color: '#999', fontSize: 13,
                }}>
                  {imageFallback}
                </div>
              ) : (
                imageFallback
              )}
            </div>
          )}
          <div style={contentStyle}>
            {badge && (
              <CardBadge position={badgePosition}>
                {badge}
              </CardBadge>
            )}
            {header && (
              <div style={{
                marginBottom: 12,
                paddingBottom: 12,
                borderBottom: `1px solid #e5e0d8`,
              }}>
                {header}
              </div>
            )}
            {children}
            {actions && (
              <div style={{
                display: 'flex', gap: 8,
                marginTop: 16, paddingTop: 12,
                borderTop: `1px solid #e5e0d8`,
              }}>
                {actions}
              </div>
            )}
          </div>
          {footer && !isBackground && (
            <div style={{
              padding: cardPadding,
              paddingTop: 0,
              borderTop: `1px solid #e5e0d8`,
              marginTop: -12,
            }}>
              {footer}
            </div>
          )}
        </>
      )}
    </div>
  );
}
